import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  mutes as mutesApi,
  sightings as sightingsApi,
  type MuteSuggestion,
  type TypeMute,
  type TypeSummary,
} from "../api/client";
import { t } from "../i18n/en";

function TypeName({ icao, description }: { icao: string | null; description: string | null }) {
  return (
    <>
      <div className="card-title">{description ?? icao ?? t.types.unknown}</div>
      {description && icao && <div className="card-sub mono">{icao}</div>}
    </>
  );
}

export default function Types() {
  const [suggestions, setSuggestions] = useState<MuteSuggestion[]>([]);
  const [muted, setMuted] = useState<TypeMute[]>([]);
  const [summary, setSummary] = useState<TypeSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const [s, m, sum] = await Promise.all([
      mutesApi.suggestions().catch(() => []),
      mutesApi.list().catch(() => []),
      sightingsApi.summary().catch(() => []),
    ]);
    setSuggestions(s);
    setMuted(m);
    setSummary(sum);
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function mute(icao: string) {
    await mutesApi.add(icao);
    await reload();
  }

  async function unmute(icao: string) {
    await mutesApi.remove(icao);
    await reload();
  }

  return (
    <div className="page">
      <Link to="/alerts" className="back">
        ‹ {t.nav.alerts}
      </Link>
      <h1>{t.types.title}</h1>
      <p className="hint">{t.types.intro}</p>

      {loading && <p className="dim">{t.loading}</p>}

      {suggestions.length > 0 && (
        <section>
          <h2>{t.types.suggestionsTitle}</h2>
          <p className="hint">{t.types.suggestionsWhy}</p>
          {suggestions.map((s) => (
            <div key={s.icao_type} className="card-row">
              <div className="card-main">
                <TypeName icao={s.icao_type} description={s.description} />
                <div className="card-sub">{t.types.seen(s.seen_count)}</div>
              </div>
              <button className="compact" onClick={() => void mute(s.icao_type)}>
                {t.types.mute}
              </button>
            </div>
          ))}
        </section>
      )}

      {muted.length > 0 && (
        <section>
          <h2>{t.types.mutedTitle}</h2>
          {muted.map((m) => (
            <div key={m.icao_type} className="card-row">
              <div className="card-main">
                <TypeName icao={m.icao_type} description={m.description} />
                <div className="card-sub">
                  {t.types.seen(m.seen_count)}
                  {m.reason === "auto_seen_count" && <span className="badge">{t.types.automatic}</span>}
                </div>
              </div>
              <button className="compact secondary" onClick={() => void unmute(m.icao_type)}>
                {t.types.unmute}
              </button>
            </div>
          ))}
        </section>
      )}

      <section>
        <h2>{t.types.historyTitle}</h2>
        {summary.length === 0 && !loading && <p className="dim">{t.types.noHistory}</p>}
        {summary.map((row) => (
          <div key={row.icao_type ?? "unknown"} className="card-row">
            <div className="card-main">
              <TypeName icao={row.icao_type} description={row.description} />
              <div className="card-sub">
                {/* Seen and notified diverge exactly when a mute is working, so both are shown
                    rather than one number that quietly changes meaning. */}
                {t.types.seenAndNotified(row.seen_count, row.notified_count)}
              </div>
            </div>
            {row.icao_type && !row.muted && (
              <button className="compact secondary" onClick={() => void mute(row.icao_type!)}>
                {t.types.mute}
              </button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
